import { computed, DestroyRef, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DecimalPipe } from '@angular/common';

import { CartRepositoryService } from '@service/cart-repository.service';
import {
  AddCartItemRequest,
  Cart,
  Cart_Item,
  GetCartInfoRequest,
  GetShippingInfosRequest,
  GetShippingInfosResponse,
  UpdateCartRequest,
} from '@proto/store/api/store/cart';
import { Carts } from '@helper/carts';
import { BaseViewModel } from '../base-view-model';
import { ProgressModel } from '../progress/progress.model';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { TagSku } from '@common/sku-tag/sku-tag.component';
import { Receipt, SalesOrder_PaymentType, SalesOrder_ReceiptMethod } from '@proto/api/orders/resources';
import { SafeStorageService } from '@service/safe-storage.service';
import { Payments } from '@helper/payments';
import { Orders } from '@helper/orders';
import { PaymentShippingInfo, PaymentShippingOverseaInfo } from '@proto/store/api/common/common';
import { ConfirmData, ConfirmDialogComponent } from '@common/confirm-dialog/confirm-dialog.component';
import { SalesOrder, SalesOrder_FrState } from '@proto/store/api/common/order_resources';
import { Links } from '@helper/links';
import { AuthStateService } from '@service/auth-state.service';
import { Constants } from '@helper/constants';
import { MetaData } from '@helper/meta-data';
import { Toast } from '@helper/toast';
import { EventTracker } from '../../analytics/event-tracker.service';
import { Recipient_Type } from '@proto/api/common/common';
import { CreateOrderRequest_Item } from '@proto/store/api/store/orders/orders';

import { Decimal } from 'decimal.js-light';
import { finalize, Subject } from 'rxjs';
import { isEqual } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class CartModel extends BaseViewModel<GetShippingInfosResponse | undefined> {
  readonly itemCount = signal(Constants.MOBILE_CART_LOAD_MORE_COUNT);
  readonly cartAdded = new Subject<Cart_Item>();
  readonly cartReplaced = new Subject<boolean>();
  readonly cartLoaded = new Subject<void>();
  readonly cart: WritableSignal<Cart>;
  readonly cartResult = computed(
    () => Carts.getCartResult(this.cart(), this.selectedInfo(), this.apiResult()?.cartInfo),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly cartItems = computed(() => this.cart().items.slice(0, this.itemCount()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly totalQtyDisplay = computed(() => this.cart().items.reduce((sum, item) => sum + item.quantity, 0), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly selectAll = computed(
    () => {
      const items = this.cart().items;
      return !!items.length && !items.some(item => !item.selected);
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly productIdSet = computed(() => new Set(this.cart().items.map(i => i.meta?.productId)), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly localShippingInfo: Signal<PaymentShippingInfo[]> = computed(
    () => {
      const excludedTypes = new Set<SalesOrder_PaymentType>();
      excludedTypes.add(SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED);
      excludedTypes.add(SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA);
      excludedTypes.add(SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA_CTBC);
      if (this.detectService.isFb()) {
        excludedTypes.add(SalesOrder_PaymentType.PAYMENT_TYPE_LINE_PAY);
      }
      return this.apiResult()?.shippingInfo?.paymentShippingInfos.filter(p => !excludedTypes.has(p.type)) ?? [];
    },
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly showOverseaWarning = signal(false);
  readonly overseaCountry = signal('');
  readonly overseaShippingInfo: Signal<PaymentShippingOverseaInfo | undefined> = computed(
    () => this.apiResult()?.shippingInfo?.paymentShippingOverseaInfos.find(p => p.name === this.overseaCountry()),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly selectedShippingType = signal(SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED);
  readonly selectedInfo: Signal<PaymentShippingInfo | undefined> = computed(
    () =>
      this.overseaShippingInfo()?.info ??
      this.apiResult()?.shippingInfo?.paymentShippingInfos.find(p => p.type === this.selectedShippingType()),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly shippingFee = computed(() => {
    const selected = this.selectedInfo();
    if (!selected) {
      return $localize`未選擇`;
    }
    return this.decimalPipe.transform(this.cartResult().footerInfo.shippingFee);
  });
  readonly feeDiff = computed(() => {
    if (new Decimal(this.cartResult().footerInfo.feeDiff || '0').lessThanOrEqualTo(0)) {
      return '';
    }
    return this.cartResult().footerInfo.feeDiff;
  });
  readonly feeDiffNotice = computed(() => {
    if (!this.feeDiff()) {
      return '';
    }
    return $localize`再購買${this.feeDiff()}元享運費優惠`;
  });
  readonly balance = computed(() => {
    const value = this.cartResult().footerInfo.balanceDeduct;
    return !Orders.isOversea(this.selectedShippingType()) && !!Number(value) ? value : '';
  });
  readonly usdTotal = computed(() => {
    if (!Orders.isOversea(this.selectedShippingType())) {
      return '';
    }
    const totalUsd = this.selectedInfo()?.totalUsd;
    if (totalUsd) {
      return totalUsd;
    }
    return Carts.getUsdTotal(
      this.cartResult().footerInfo.totalPrice,
      this.apiResult()?.shippingInfo?.exchangeRateUsdNtd
    );
  });
  readonly exceedItems = computed(
    () => this.cart().items.filter(i => this.cartResult().warnList.has(i.skuId) && i.selected),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly checkOversea = computed(
    () =>
      Orders.isOversea(this.selectedShippingType()) && this.cart().items.some(i => i.prohibitOverseaSale && i.selected)
  );
  readonly recipientType = computed(() => Payments.payTypeToRecipientType(this.selectedShippingType()));
  // 發票類型：使用最後一次顧客訂單成功的發票類型，如果沒有預設，個人-電子發票。
  readonly receipt = signal(
    Receipt.fromPartial({ receiptMethod: SalesOrder_ReceiptMethod.RECEIPT_METHOD_UNSPECIFIED }),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly returnAgree = signal(true);
  // credit card
  readonly cardNumber = signal('');
  readonly yearMonth = signal('');
  readonly cvv = signal('');
  // line pay
  readonly linePayOrderNo = signal('');
  readonly linePayRedirectLink = signal('');

  constructor(
    private lativRepository: CartRepositoryService,
    private authService: AuthStateService,
    private eventTracker: EventTracker,
    private progressModel: ProgressModel,
    private decimalPipe: DecimalPipe,
    localService: SafeStorageService
  ) {
    super(undefined, true);
    authService.logoutEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cart.set(Cart.fromPartial({})));
    this.cart = signal(localService.getCart() ?? Cart.fromPartial({}), { equal: (a, b) => isEqual(a, b) });
    effect(() => localService.setCart(this.cart()));
    effect(() => {
      if (
        this.apiResult()?.shippingInfo &&
        !this.apiResult()?.shippingInfo?.paymentShippingInfos.some(s => s.type === this.selectedShippingType()) &&
        !this.apiResult()?.shippingInfo?.paymentShippingOverseaInfos.some(
          s => s.info?.type === this.selectedShippingType()
        )
      ) {
        this.selectedShippingType.set(SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED);
      } else if (this.localShippingInfo().find(i => i.type === this.selectedShippingType())?.overLimitRemind) {
        this.selectedShippingType.set(SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED);
      }
      if (!Orders.isOversea(this.selectedShippingType())) {
        this.overseaCountry.set('');
      }
    });
  }

  getCart(ref?: DestroyRef, init?: boolean): void {
    (ref ? this.lativRepository.getCart().pipe(takeUntilDestroyed(ref)) : this.lativRepository.getCart()).subscribe({
      next: cart => {
        if (cart.items.length && init) {
          this.eventTracker.onShoppingCart(
            cart.items.map(item => ({
              id: item.skuId,
              name: item.meta?.name ?? '',
              category: item.meta?.big?.name ?? '',
              quantity: item.quantity,
              price: item.meta?.promotionPrice || item.meta?.originPrice,
              promotionId: '',
              promotionName: '',
            }))
          );
        }
        this.cart.set(cart);
        this.cartLoaded.next();
        this.setApiError(undefined);
      },
      error: err => this.setApiError(err),
    });
  }

  // 到選擇付款方式，確認訂單，選擇發票，購物車都要有商品
  checkCart(): boolean {
    return !!this.cart().items.filter(item => item.selected).length;
  }

  hasExceedItem(): boolean {
    return !!this.exceedItems().length;
  }

  // 到確認訂單，要先選擇付款與運送方式
  checkSelectShip(): boolean {
    return !!this.apiResult() && this.selectedShippingType() !== SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED;
  }

  // 到確認訂單，如果選擇的運送方式沒有資料，要導去新增地址.
  checkAddress(): boolean {
    const recipientType = this.recipientType();
    if (recipientType === Recipient_Type.TYPE_PICKUP_STORE) {
      // 超商會看所選的超商有沒有地址, 沒有就新增該超商的地址.
      return !!this.apiResult()?.recipientInfo?.recipientInfos.some(
        i => i.recipient?.storeAddress?.type === Orders.storeType(this.selectedShippingType())
      );
    } else if (recipientType === Recipient_Type.TYPE_TW_DELIVERY) {
      const island = this.selectedShippingType() === SalesOrder_PaymentType.PAYMENT_TYPE_CASH_ON_DELIVERY;
      if (island) {
        return !!this.apiResult()?.recipientInfo?.recipientInfos.some(
          i => i.recipient?.address && !i.recipient?.address.isIsland
        );
      }
      return !!this.apiResult()?.recipientInfo?.recipientInfos.some(i => i.recipient?.address);
    } else if (recipientType === Recipient_Type.TYPE_OVERSEA) {
      // 海外會看所選的國別有沒有地址, 沒有就要新增該國別的地址.
      return !!this.apiResult()?.recipientInfo?.recipientInfos.some(
        i => i.recipient?.overseaAddress?.country === this.overseaCountry()
      );
    }
    return false;
  }

  getShippingInfo(ref: DestroyRef, callback?: () => void): void {
    const items = this.cart().items.filter(item => item.selected);
    if (!items.length) {
      return;
    }
    this.progressModel.show();
    this.lativRepository
      .getShippingInfos(GetShippingInfosRequest.fromPartial({ items }))
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => this.progressModel.hide())
      )
      .subscribe({
        next: resp => {
          this.setApiResult(resp);
          if (resp.receiptInfo) {
            const receipt = resp.receiptInfo;
            if (this.receipt().receiptMethod === SalesOrder_ReceiptMethod.RECEIPT_METHOD_UNSPECIFIED) {
              if (receipt.receiptMethod === SalesOrder_ReceiptMethod.RECEIPT_METHOD_UNSPECIFIED) {
                receipt.receiptMethod = SalesOrder_ReceiptMethod.RECEIPT_METHOD_MEMBER_CARRIER;
              }
              this.receipt.set(
                Receipt.fromPartial({
                  receiptMethod: receipt.receiptMethod,
                  taxId: receipt.taxId,
                  donateUnitId: receipt.donateUnitId,
                  mobileBarcode: receipt.mobileBarcode,
                })
              );
            }
          }
          if (!this.selectedShippingType()) {
            const info = this.localShippingInfo().find(i => i.type === resp.shippingInfo?.lastSelectedType);
            if (info && !info.overLimitRemind) {
              this.selectedShippingType.set(info.type);
            } else {
              const first = this.localShippingInfo().find(i => !i.overLimitRemind);
              this.selectedShippingType.set(first?.type || SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED);
            }
          }
          if (callback) {
            callback();
          }
        },
        error: err => this.setApiError(err),
      });
  }

  addCart(item: Cart_Item, toast: MatSnackBar, replaceSkuId = ''): void {
    const existedQuantity = replaceSkuId ? 0 : (this.cart().items.find(it => it.skuId === item.skuId)?.quantity ?? 0);
    if (item.quantity + existedQuantity > Math.min(Constants.MAX_CART_ITEM_COUNT, item.inventory)) {
      Toast.open(toast, $localize`已超過可購買數量`);
      return;
    }
    if (!replaceSkuId) {
      this.cartAdded.next(item);
    }
    this.eventTracker.onAddToShoppingCart({
      id: item.skuId,
      name: item.meta?.name ?? '',
      category: item.meta?.big?.name ?? '',
      quantity: item.quantity,
      price: item.meta?.promotionPrice || item.meta?.originPrice,
      promotionId: '',
      promotionName: '',
    });
    this.cart.update(v => {
      const model = Cart.fromPartial(v);
      const existedIndex = model.items.findIndex(i => i.skuId === item.skuId);
      if (existedIndex >= 0) {
        const quantity = model.items[existedIndex].quantity;
        model.items[existedIndex] = Cart_Item.fromPartial(item);
        model.items[existedIndex].quantity = replaceSkuId ? item.quantity : quantity + item.quantity;
      } else {
        model.items.push(item);
      }
      return model;
    });
    // support adding cart item without login
    if (this.authService.isLogin()) {
      this.lativRepository
        .addCartItem(
          AddCartItemRequest.fromPartial({
            item,
            replaceSkuId,
          })
        )
        .subscribe(cart => this.cart.set(cart));
    } else {
      this.lativRepository
        .getCartInfo(
          GetCartInfoRequest.fromPartial({
            items: this.cart().items,
          })
        )
        .subscribe(cart => this.cart.set(cart));
    }
  }

  replaceCartItem(sku: TagSku, item: Cart_Item): void {
    this.cart.update(v => {
      const model = Cart.fromPartial(v);
      const index = model.items.findIndex(it => it.skuId === item.skuId);
      if (item.skuId === sku.skuId) {
        model.items[index].quantity = Math.min(
          item.quantity,
          Math.min(model.items[index].inventory, Constants.MAX_CART_ITEM_COUNT)
        );
        return model;
      }
      const existSkuIndex = model.items.findIndex(it => it.skuId === sku.skuId);
      if (existSkuIndex >= 0) {
        // merge into existed sku
        model.items[existSkuIndex].image = sku.color.image?.img360 ?? '';
        model.items[existSkuIndex].quantity = Math.min(
          Math.max(1, model.items[existSkuIndex].quantity + item.quantity),
          Math.min(model.items[existSkuIndex].inventory, Constants.MAX_CART_ITEM_COUNT)
        );
        model.items[existSkuIndex].selected = true;
        model.items[existSkuIndex].inventory = sku.inventory;
        model.items = model.items.filter(it => it.skuId !== item.skuId);
      } else if (index >= 0) {
        model.items[index].skuId = sku.skuId;
        model.items[index].colorName = sku.color.color?.name ?? '';
        model.items[index].sizeName = sku.size.name;
        model.items[index].image = sku.color.image?.img360 ?? '';
        model.items[index].selected = true;
        model.items[index].quantity = Math.max(
          1,
          Math.min(item.quantity, Math.min(sku.inventory, Constants.MAX_CART_ITEM_COUNT))
        );
        model.items[index].inventory = sku.inventory;
        this.eventTracker.onAddToShoppingCart({
          id: item.skuId,
          name: item.meta?.name ?? '',
          category: item.meta?.big?.name ?? '',
          quantity: item.quantity,
          price: item.meta?.promotionPrice || item.meta?.originPrice,
          promotionId: '',
          promotionName: '',
        });
      }
      return model;
    });
    this.cartReplaced.next(sku.skuId != item.skuId);
    this.lativRepository
      .updateCart(UpdateCartRequest.fromPartial({ items: this.cart().items }))
      .subscribe(() => this.getCart(this.destroyRef));
  }

  changeItemCount(count: number, item: Cart_Item): void {
    this.cart.update(v => {
      const model = Cart.fromPartial(v);
      const index = model.items.findIndex(it => it.skuId === item.skuId);
      if (index >= 0) {
        model.items[index].selected = true;
        model.items[index].quantity = count;
      }
      return model;
    });
    this.lativRepository.updateCart(UpdateCartRequest.fromPartial({ items: this.cart().items })).subscribe();
  }

  deleteCartItem(item: Cart_Item, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.eventTracker.onRemoveFromShoppingCart({
      id: item.skuId,
      name: item.meta?.name ?? '',
      category: item.meta?.big?.name ?? '',
      quantity: item.quantity,
      price: item.meta?.promotionPrice || item.meta?.originPrice,
      promotionId: '',
      promotionName: '',
    });
    this.cart.update(v => {
      const model = Cart.fromPartial(v);
      model.items = model.items.filter(it => it.skuId !== item.skuId);
      return model;
    });
    // support delete cart item without login
    if (this.authService.isLogin()) {
      this.lativRepository.updateCart(UpdateCartRequest.fromPartial({ items: this.cart().items })).subscribe();
    }
  }

  updateSelectAll(): void {
    const all = this.selectAll();
    this.cart.update(cart => {
      const model = Cart.fromPartial(cart);
      model.items.forEach(v => (v.selected = !all));
      return model;
    });
    this.lativRepository.updateCart(UpdateCartRequest.fromPartial({ items: this.cart().items })).subscribe();
  }

  changeCheckedItem(checked: boolean, item: Cart_Item): void {
    this.cart.update(cart => {
      const model = Cart.fromPartial(cart);
      const index = model.items.findIndex(it => it.skuId === item.skuId);
      if (index >= 0) {
        model.items[index].selected = checked;
      }
      return model;
    });
    this.lativRepository.updateCart(UpdateCartRequest.fromPartial({ items: this.cart().items })).subscribe();
  }

  onPromotion(item: Cart_Item, id: string, category: string): void {
    if (!id || !item.published) {
      return;
    }
    this.router.navigate([RouteHelper.salesEvent(id, category)]);
  }

  onProduct(item: Cart_Item, link: string): void {
    if (!item.published || !link) {
      return;
    }
    Links.openInNewWindow(link);
  }

  overseaChange(name: string): void {
    this.overseaCountry.set(name);
    this.selectedShippingType.set(
      this.overseaShippingInfo()?.info?.type ?? SalesOrder_PaymentType.PAYMENT_TYPE_UNSPECIFIED
    );
  }

  checkShippingInfo(dialog: MatDialog, info: PaymentShippingInfo | undefined): boolean {
    if (!info) {
      return true;
    }
    if (info?.overLimitRemind) {
      ConfirmDialogComponent.open(
        dialog,
        new ConfirmData({
          singleBtnTitle: $localize`知道了`,
          disableClose: true,
          content: info.overLimitRemind,
        })
      );
      return true;
    }
    return false;
  }

  reset(order: SalesOrder | undefined, items: CreateOrderRequest_Item[]): void {
    if (
      !order ||
      ((order.paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD ||
        order.paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_CREDIT_CARD_CTBC ||
        order.paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA ||
        order.paymentType === SalesOrder_PaymentType.PAYMENT_TYPE_OVERSEA_CTBC) &&
        order.state === SalesOrder_FrState.FR_STATE_CANCELED)
    ) {
      // order does not exist or credit card payment failed, we don't clear cart.
      return;
    }
    const skuIds = new Set(items.map(item => item.skuId));
    this.cart.update(cart => {
      const model = Cart.fromPartial(cart);
      model.items = model.items.filter(item => !skuIds.has(item.skuId));
      return model;
    });
    this.returnAgree.set(true);
    this.clearCardInfo();
  }

  clearCardInfo(): void {
    this.cardNumber.set('');
    this.yearMonth.set('');
    this.cvv.set('');
  }

  onCheckout(): void {
    this.eventTracker.onCheckout(
      this.cart()
        .items.filter(i => i.selected)
        .map(item => ({
          id: item.skuId,
          name: item.meta?.name ?? '',
          category: item.meta?.big?.name ?? '',
          quantity: item.quantity,
          price: item.meta?.promotionPrice || item.meta?.originPrice,
          promotionId: '',
          promotionName: '',
        })),
      Number(this.cartResult().footerInfo.totalPrice || 0)
    );
  }

  onShipping(): void {
    this.eventTracker.onShipping(
      this.cart()
        .items.filter(i => i.selected)
        .map(item => ({
          id: item.skuId,
          name: item.meta?.name ?? '',
          category: item.meta?.big?.name ?? '',
          quantity: item.quantity,
          price: item.meta?.promotionPrice || item.meta?.originPrice,
          promotionId: '',
          promotionName: '',
        })),
      Number(this.cartResult().footerInfo.totalPrice || 0),
      Payments.payTypeDisplay(this.selectedShippingType())
    );
  }

  override getMetaByUrl(segments: string[]): MetaData | undefined {
    if (segments.length === 1 && segments[0] === RoutePaths.CART) {
      return new MetaData($localize`購物車`);
    } else if (segments[0] === RoutePaths.CART && segments[1] === RoutePaths.SHIPPING) {
      return new MetaData($localize`付款及運送方式`);
    }
    return undefined;
  }
}
